import { useState } from "react";
import { Edit, Trash, List, Linkedin } from "lucide-react";
import { PageHeader } from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { useTranslation } from "react-i18next";
import { NotificationSubscriptionButton } from "@/components/notifications/NotificationSubscriptionButton";
import { FavoriteButton } from "@/components/favorites/FavoriteButton";
import { CandidateListsModal } from "@/components/candidates/CandidateListsModal";
import { BackButton } from "@/components/ui/BackButton";
import { SuperAdminReanalyzeButton } from "@/components/admin/SuperAdminReanalyzeButton";

interface CandidateDetailHeaderProps {
  isEditing: boolean;
  onEdit: () => void;
  onSave: () => void;
  onCancel: () => void;
  handleDelete: () => void;
  candidateName: string;
  candidateId: string;
  linkedinId?: string | null;
  showLinkedInButton?: boolean;
  onGetLinkedInProfile?: () => void;
}

export const CandidateDetailHeader = ({
  isEditing,
  onEdit,
  onSave,
  onCancel,
  handleDelete,
  candidateName,
  candidateId,
  linkedinId,
  showLinkedInButton = false,
  onGetLinkedInProfile
}: CandidateDetailHeaderProps) => {
  const { t } = useTranslation();
  const [isListsModalOpen, setIsListsModalOpen] = useState(false);
  
  return (
    <>
      <div className="flex justify-between items-center mb-3">
        <BackButton
          to="/candidates"
          label={t("candidates.profile.backToCandidates")}
        />
        <div className="flex gap-1.5">
          {showLinkedInButton && onGetLinkedInProfile && (
            <Button
              variant="ghost"
              size="sm"
              className="h-8 gap-1.5 text-xs rounded-sm"
              onClick={onGetLinkedInProfile}
              title="Get LinkedIn Profile"
            >
              <Linkedin className="h-3.5 w-3.5" />
              LinkedIn
            </Button>
          )}
          <Button
            variant="ghost"
            size="icon"
            className="h-8 w-8 rounded-sm"
            onClick={() => setIsListsModalOpen(true)}
            title={t('candidates.actions.lists', 'Manage Lists')}
          >
            <List className="h-3.5 w-3.5" />
          </Button>
          <FavoriteButton
            entityType="candidate"
            entityId={candidateId}
            entityName={candidateName}
            variant="ghost"
            size="icon"
          />
          <NotificationSubscriptionButton
            entityType="candidate"
            entityId={candidateId}
            entityName={candidateName}
            variant="ghost"
            size="icon"
          />
          <SuperAdminReanalyzeButton
            entityType="candidate"
            entityId={candidateId}
            variant="ghost"
            className="h-8 w-8 rounded-sm"
            iconClassName="h-3.5 w-3.5"
          />
          {!isEditing ? (
            <Button
              variant="ghost"
              size="icon"
              className="h-8 w-8 rounded-sm"
              onClick={onEdit}
              title={t('candidates.actions.edit')}
            >
              <Edit className="h-3.5 w-3.5" />
            </Button>
          ) : null}
          <Button
            variant="ghost"
            size="icon"
            className="h-8 w-8 rounded-sm text-red-600 hover:text-red-700 hover:bg-red-50 dark:text-red-400 dark:hover:bg-red-950"
            onClick={handleDelete}
            title={t("candidates.actions.delete")}
          >
            <Trash className="h-3.5 w-3.5" />
          </Button>
        </div>
      </div>

      <CandidateListsModal
        isOpen={isListsModalOpen}
        onClose={() => setIsListsModalOpen(false)}
        candidateId={candidateId}
        candidateName={candidateName}
      />
    </>
  );
};
